import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DataSources from "./pages/DataSources";
import News from "./pages/News";
import TrainingResources from "./pages/TrainingResources";
import ChatPage from "./pages/ChatPage";
import AdminPage from "./pages/AdminPage";
import Sidebar from "./components/Sidebar";
import {
  useAuth,
  useClerk,
} from "@clerk/clerk-react";
import useChat from "./hooks/useChat";
import TrialBanner from "./TrialBanner";
import "./App.css";
import "./components/Footer.css";
import DisclaimerModal from "./components/DisclaimerModal";
import DisclaimerNotice from "./components/DisclaimerNotice";
import { PanelLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export default function MainApp() {
  const [trialExpired, setTrialExpired] = useState(false);
  const [trialStartDate, setTrialStartDate] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const resizeObserverLoopErr = (e) => {
      if (e.message.includes("ResizeObserver loop completed with undelivered notifications")) {
        const resizeObserverErrDiv = document.getElementById("webpack-dev-server-client-overlay-div");
        const resizeObserverErr = document.getElementById("webpack-dev-server-client-overlay");
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    };
    window.addEventListener("error", resizeObserverLoopErr);
    return () => window.removeEventListener("error", resizeObserverLoopErr);
  }, []);

  const { getToken, isSignedIn, isLoaded } = useAuth();
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();

  const { data: conversations = [], refetch: refetchConversations } = useQuery({
    queryKey: ['conversations', isSignedIn],
    queryFn: async () => {
      if (!isSignedIn) return [];
      const token = await getToken();
      if (!token) return [];
      const resp = await fetch(`${BACKEND_URL}/api/v1/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        return data.conversations || [];
      }
      throw new Error('Failed to fetch conversations');
    },
    enabled: isLoaded && !!isSignedIn,
    staleTime: 5 * 60 * 1000,
  });

  const chat = useChat({
    getToken,
    isSignedIn,
    openSignIn: () => clerk.openSignIn(),
    currentConversationId,
    fetchConversations: refetchConversations,
    hasSubscription,
    trialExpired,
    setShowPaywall,
    embedMode: false,
  });

  const { data: userStatus, isLoading: isUserStatusLoading } = useQuery({
    queryKey: ['userStatus', isSignedIn],
    queryFn: async () => {
      const headers = {};
      if (isSignedIn) {
        const token = await getToken();
        if (token) headers["Authorization"] = `Bearer ${token}`;
      }
      const resp = await fetch(`${BACKEND_URL}/api/v1/me`, {
        headers,
        credentials: "include",
      });
      if (resp.ok) {
        return resp.json();
      }
      throw new Error('Failed to fetch user status');
    },
    enabled: isLoaded,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (userStatus) {
      if (userStatus.trialExpired !== undefined) {
        setTrialExpired(userStatus.trialExpired);
      }
      if (userStatus.trialStartDate) {
        setTrialStartDate(userStatus.trialStartDate);
      }
      setIsSuperUser(!!userStatus.isSuperUser);
      if (isSignedIn && userStatus.subscription) {
        const status = userStatus.subscription.status;
        if (status === "active" || status === "trialing") {
          setHasSubscription(true);
          setShowPaywall(false);
        } else {
          setHasSubscription(false);
        }
      } else {
        setHasSubscription(false);
      }
    }
  }, [userStatus, isSignedIn, isLoaded]);

  const {
    messages, setMessages,
    setSessionId,
  } = chat;

  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^\/c\/(.+)$/);
      if (match) {
        loadConversation(match[1]);
      } else {
        setCurrentConversationId(null);
        chat.handleNewChat();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  const handleNewChat = () => {
    setCurrentConversationId(null);
    chat.handleNewChat();
    navigate("/");
  };

  const loadConversation = async (convId) => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}/api/v1/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setCurrentConversationId(data._id);
        setSessionId(data.threadId || `session_${data._id}`);
        const loadedMessages = data.messages.map(m => ({
          sender: m.role === "user" ? "user" : "bot",
          content: m.content
        }));
        setMessages(loadedMessages);
        navigate(`/c/${data._id}`);
      }
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const deleteConversation = async (convId) => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}/api/v1/conversations/${convId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        queryClient.setQueryData(['conversations', isSignedIn], (oldList) =>
          oldList ? oldList.filter(c => c._id !== convId) : []
        );
        if (currentConversationId === convId) {
          handleNewChat();
        }
      } else {
        console.error("Failed to delete conversation");
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      setHasSubscription(true);
      setShowPaywall(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          content: "🎉 **Thank you for subscribing!** Your account has been upgraded to Pro. You now have unlimited messaging.",
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openUpgradePaywall = () => {
    if (!isSignedIn) return;
    setShowPaywall(true);
  };

  const handleValidatePromo = async (plan = "yearly", promotionCode = "") => {
    if (!isSignedIn) {
      throw new Error("Please sign in to apply a promotion code.");
    }
    const planKey = typeof plan === "string" && plan ? plan : "yearly";
    const promo = typeof promotionCode === "string" ? promotionCode.trim() : "";
    if (!promo) {
      throw new Error("Enter a promotion code.");
    }

    const token = await getToken();
    const resp = await fetch(`${BACKEND_URL}/api/v1/billing/promo/validate`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        plan: planKey,
        promotionCode: promo,
      }),
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data.message || "Invalid or expired promotion code.");
    }
    return data;
  };

  const handleSubscribe = async (plan = "yearly", promotionCode = "") => {
    if (!isSignedIn) return;
    const planKey = typeof plan === "string" && plan ? plan : "yearly";
    const promo = typeof promotionCode === "string" ? promotionCode.trim() : "";
    setIsCheckoutLoading(true);

    try {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}/api/v1/billing/checkout`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          plan: planKey,
          ...(promo ? { promotionCode: promo } : {}),
          successUrl: `${window.location.origin}${window.location.pathname}?success=true`,
          cancelUrl: `${window.location.origin}${window.location.pathname}?canceled=true`,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.message || "Failed to create checkout session");
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert(err.message || "Failed to start checkout process. Please try again.");
      setIsCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!isSignedIn) return;
    setIsPortalLoading(true);

    try {
      const token = await getToken();
      const resp = await fetch(`${BACKEND_URL}/api/v1/billing/portal`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          returnUrl: window.location.href,
        })
      });

      if (!resp.ok) {
        throw new Error("Failed to create portal session");
      }

      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No portal URL returned");
      }
    } catch (err) {
      console.error("Portal error:", err);
      alert("Failed to open subscription portal. Please try again.");
      setIsPortalLoading(false);
    }
  };

  return (
    <>
      <div className="app-layout">
        <nav aria-label="Skip links">
          <a
            href="#main-content"
            className="skip-nav"
            aria-label="Skip to main content"
            onClick={(e) => {
              const mainEl = document.getElementById("main-content");
              if (!mainEl) return;
              e.preventDefault();
              mainEl.focus();
              mainEl.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Skip to main content
          </a>
        </nav>
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          handleNewChat={handleNewChat}
          conversations={conversations}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          currentConversationId={currentConversationId}
          loadConversation={loadConversation}
          hasSubscription={hasSubscription}
          isSuperUser={isSuperUser}
          handleSubscribe={openUpgradePaywall}
          isCheckoutLoading={isCheckoutLoading}
          handleManageSubscription={handleManageSubscription}
          isPortalLoading={isPortalLoading}
          deleteConversation={deleteConversation}
          onOpenDisclaimer={() => setShowDisclaimerModal(true)}
          isEmbedMode={false}
        />

        <main
          id="main-content"
          tabIndex={-1}
          className={`main-content ${sidebarOpen ? '' : 'sidebar-closed'}`}
        >
          {!sidebarOpen && (
            <button className="toggle-btn floating desktop-hidden" onClick={() => setSidebarOpen(true)} title="Open sidebar" aria-label="Open sidebar">
              <PanelLeft size={18} aria-hidden="true" />
            </button>
          )}
          {location.pathname !== "/admin" && (
            <TrialBanner
              trialStartDate={trialStartDate}
              trialExpired={trialExpired}
              isSignedIn={isSignedIn}
              hasSubscription={hasSubscription}
              isSuperUser={isSuperUser}
              onSubscribe={openUpgradePaywall}
              isCheckoutLoading={isCheckoutLoading}
              isChatActive={!(messages.length <= 1 && currentConversationId == null && location.pathname === "/")}
            />
          )}
          <Routes>
            <Route path="/sources" element={<DataSources />} />
            <Route path="/news" element={<News />} />
            <Route path="/training" element={<TrainingResources />} />
            <Route
              path="/admin"
              element={
                <AdminPage
                  isSuperUser={isSuperUser}
                  isStatusLoading={isUserStatusLoading}
                />
              }
            />
            <Route path="/c/:conversationId" element={
              <ChatPage
                chat={chat}
                showPaywall={showPaywall}
                setShowPaywall={setShowPaywall}
                handleSubscribe={handleSubscribe}
                handleValidatePromo={handleValidatePromo}
                isCheckoutLoading={isCheckoutLoading}
                currentConversationId={currentConversationId}
                hasSubscription={hasSubscription}
                trialExpired={trialExpired}
                loadConversation={loadConversation}
                handleNewChat={handleNewChat}
                embedMode={false}
              />
            } />
            <Route path="*" element={
              <ChatPage
                chat={chat}
                showPaywall={showPaywall}
                setShowPaywall={setShowPaywall}
                handleSubscribe={handleSubscribe}
                handleValidatePromo={handleValidatePromo}
                isCheckoutLoading={isCheckoutLoading}
                currentConversationId={currentConversationId}
                hasSubscription={hasSubscription}
                trialExpired={trialExpired}
                loadConversation={loadConversation}
                handleNewChat={handleNewChat}
                embedMode={false}
              />
            } />
          </Routes>

          {location.pathname !== "/admin" && (
            <footer className="app-footer">
              <DisclaimerNotice onOpenFull={() => setShowDisclaimerModal(true)} />
              <div className="footer-branding">
                <img src={`${process.env.PUBLIC_URL}/revelo.png`} alt="Revelo" className="revelo-logo" />
                <span className="copyright-text">© 2026 iAccessible | Powered by Revelo</span>
              </div>
            </footer>
          )}
        </main>
      </div>

      <DisclaimerModal
        isVisible={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
      />
    </>
  );
}
